import React, { useContext, useState, useMemo } from "react";
import "./StudentDetails.scss";
import { sessionStore } from "../../../../contexts/SessionContext";
import { useParams } from "react-router";
import { defineMessages, useIntl } from "react-intl";
import Grid from "../../../../components/Grid/Grid";
import GridColumn from "../../../../components/Grid/GridColumn";
import PageWrapper from "../../../../components/PageWrapper/PageWrapper";
import PageHeader from "../../../../components/PageHeader/PageHeader";
import ContainerBlock from "../../../../design-system-components/ContainerBlock/ContainerBlock";
import SpecimenHelper from "../SpecimenHelper/SpecimenHelper";
import {
    ClassroomAnalytics,
    Dashboard,
} from "../../../../interfaces/Dashboard";
import TimeSpentOnModules from "./TimeSpentOnModules/TimeSpentOnModules";
import { ParamTypes } from "./Types";
import Select from "../../../../components/Select/Select";
import {
    AggregatedInfo,
    Cluster,
    generateClustering,
    getModuleName,
} from "../../../Clustering/utils";
import StudentCluster from "./StudentCluster/StudentCluster";
import ObjectivesProgression from "./ObjectivesProgression/ObjectivesProgression";
import { Link } from "react-router-dom";
import { Icon } from "@evidenceb/athena-common/design-system/Icon";
import useScrollTopOnLocationChange from "../../../../hooks/useScrollTopOnLocationChange ";
import { getStudent } from "../../../../utils/dataRetrieval";
import { fullName } from "../../../../utils/format";
import TabTitle from "../../../../components/TabTitle/TabTitle";
import { useData } from "../../../../hooks/useData";
import { useDashboardRouter } from "../routing";
import { LegacyLoader } from "../../../../components/Loader/Loader";

interface Props {
    dashboard: Dashboard;
    dashboardLoaded: Boolean;
}

export interface ModulesListEntry {
    id: string;
    name: string;
}

const StudentDetails = ({ dashboard, dashboardLoaded }: Props) => {
    const intl = useIntl();
    useScrollTopOnLocationChange();
    const { studentId, collectionId, moduleId } = useParams<ParamTypes>();
    const {
        progressionOverview: getProgressionOverviewUrl,
        clustering: getClusteringUrl,
    } = useDashboardRouter();
    const {
        session: { specimen, classrooms },
    } = useContext(sessionStore);
    const data = useData();

    const classroom = dashboard
        ? getClassroom(dashboard, collectionId) ||
          findClassroom(dashboard, studentId)
        : undefined;

    const classroomId = classroom?.id || collectionId;

    const [currentModuleId, setCurrentModuleId] = useState<string>(
        moduleId ?? data?.modules[0].id
    );

    const clustersInfo = useMemo<
        (AggregatedInfo | Cluster)[] | undefined
    >(() => {
        if (
            !currentModuleId ||
            !dashboard ||
            !dashboard.classrooms ||
            !dashboard.clustering
        )
            return;
        return generateClustering(
            dashboard.clustering,
            dashboard.classrooms,
            classroomId,
            currentModuleId,
            intl
        );
    }, [currentModuleId, dashboard, classroomId, intl]);

    const student = useMemo(() => {
        if (!classrooms) return;
        return getStudent(studentId, classrooms);
    }, [classrooms, studentId]);

    const studentAnalytics = useMemo(() => {
        if (!classroom) return;
        const currentModule = classroom.modulesList.find(
            (m) => m.id === currentModuleId
        );
        if (!currentModule) return;
        const analytics = currentModule.students[studentId];
        if (!analytics) return;
        return analytics;
    }, [classroom, currentModuleId, studentId]);

    const studentCluster = useMemo<Cluster | undefined>(() => {
        return clustersInfo?.find((cluster) => {
            return cluster.membersInfo.some((member) => {
                return member.id === studentId;
            });
        });
    }, [clustersInfo, studentId]);

    const modulesList: ModulesListEntry[] | undefined = classroom?.modulesList
        .map((module, _i) => ({
            id: module.id,
            name: getModuleName(module.id, data),
        }))
        .filter(({ name }) => name != "");

    if (dashboardLoaded == false && !studentAnalytics) {
        console.log("waiting analytics for this student...");
        return (
            <div className="student-details">
                <TabTitle title={(student && fullName(student)) ?? ""} />

                <PageWrapper>
                    <PageHeader
                        title={{
                            content: (student && fullName(student)) ?? "",
                        }}
                    />
                    <LegacyLoader />
                </PageWrapper>
            </div>
        );
    }
    return (
        <div className="student-details">
            <TabTitle title={(student && fullName(student)) ?? ""} />

            <PageWrapper>
                <PageHeader
                    title={{ content: (student && fullName(student)) ?? "" }}
                />
                <Grid>
                    <GridColumn col={8}>
                        {classroom && classroom.modulesList.length > 0 && (
                            <Select
                                label={intl.formatMessage(
                                    messages.selectModule
                                )}
                                className="student-details__modules-select"
                                onSelectOption={(value) => {
                                    setCurrentModuleId(value);
                                }}
                                selectedOption={getModuleName(
                                    currentModuleId,
                                    data
                                )}
                            >
                                {modulesList
                                    ? modulesList.map((module, i) => (
                                          <option key={i} value={module.id}>
                                              {module.name}
                                          </option>
                                      ))
                                    : ""}
                            </Select>
                        )}
                        <ContainerBlock className="student-details__block">
                            <h2>
                                {intl.formatMessage(messages.objectiveDetails)}
                            </h2>
                            {studentAnalytics ? (
                                <ObjectivesProgression
                                    student={studentAnalytics}
                                />
                            ) : (
                                <p className="student-details__noData">
                                    {intl.formatMessage(
                                        messages.noModuleCluster
                                    )}
                                </p>
                            )}
                            {classroom && (
                                <div className="student-details__link">
                                    <Link
                                        to={getProgressionOverviewUrl(
                                            classroom.id,
                                            currentModuleId
                                        )}
                                    >
                                        {intl.formatMessage(
                                            messages.seeProgressOverviewPage,
                                            {
                                                classroomName: classroom.name,
                                            }
                                        )}
                                    </Link>
                                    <Icon path="chevron_right" />
                                </div>
                            )}
                        </ContainerBlock>
                        <ContainerBlock className="student-details__block">
                            <h2>{intl.formatMessage(messages.aiNeedGroups)}</h2>
                            {studentCluster && (
                                <>
                                    <StudentCluster
                                        studentCluster={studentCluster}
                                        studentId={studentId}
                                    />
                                    {classroom && (
                                        <div className="student-details__link">
                                            <Link
                                                to={{
                                                    pathname: getClusteringUrl(
                                                        classroom.id,
                                                        currentModuleId
                                                    ),
                                                    state: {
                                                        studentClusterId:
                                                            studentCluster.id,
                                                    },
                                                }}
                                            >
                                                {intl.formatMessage(
                                                    messages.seeClusteringPage,
                                                    {
                                                        classroomName:
                                                            classroom.name,
                                                    }
                                                )}
                                            </Link>
                                            <Icon path="chevron_right" />
                                        </div>
                                    )}
                                </>
                            )}
                            {!clustersInfo && (
                                <p className="student-details__noData">
                                    {intl.formatMessage(
                                        messages.noClustersInfo
                                    )}
                                </p>
                            )}
                        </ContainerBlock>
                    </GridColumn>
                    <GridColumn col={4}>
                        <ContainerBlock className="student-details__block">
                            <h2>{intl.formatMessage(messages.generalData)}</h2>
                            {classroom && studentId && modulesList && (
                                <TimeSpentOnModules
                                    classroom={classroom}
                                    studentId={studentId}
                                    currentModuleId={currentModuleId}
                                    modulesList={modulesList}
                                />
                            )}
                        </ContainerBlock>
                    </GridColumn>
                </Grid>
            </PageWrapper>
            {specimen && (
                <SpecimenHelper
                    dialogTitle={intl.formatMessage(messages.specimenTitle)}
                    dialogDescription={intl.formatMessage(
                        messages.specimenDescription
                    )}
                />
            )}
        </div>
    );
};

export default StudentDetails;

const getClassroom = (
    dashboard: Dashboard,
    id: string
): ClassroomAnalytics | undefined => {
    return dashboard.classrooms.find((classroom) => classroom.id === id);
};

const findClassroom = (
    dashboard: Dashboard,
    studentId: string
): ClassroomAnalytics | undefined => {
    const classrooms = dashboard.classrooms.filter(
        (classroom) =>
            classroom.modulesList.filter((m) => studentId in m.students).flat()
                .length > 0
    );
    return classrooms[0];
};

const messages = defineMessages({
    objectiveDetails: {
        id: "objective-details",
        defaultMessage: "Objective details",
    },
    aiNeedGroups: {
        id: "student-details-aiNeedGroups",
        defaultMessage: "AI need groups",
    },
    seeProgressOverviewPage: {
        id: "student-details-seeProgressOverviewPage",
        defaultMessage: "See the class progress page",
    },
    seeClusteringPage: {
        id: "student-details-seeClusteringPage",
        defaultMessage: "See the AI group page of {classroomName}",
    },
    generalData: {
        id: "student-details-generalData",
        defaultMessage: "General data",
    },
    selectModule: {
        id: "dashboard-common-select-module",
        defaultMessage: "Select a module",
    },
    noModuleCluster: {
        id: "dashboard-clustering-noClustersForClassAndModudle",
        defaultMessage: "No clusters for this module",
    },
    noClustersInfo: {
        id: "student-details-noClustersInfo",
        defaultMessage:
            "Students in this class did not complete enough exercises in this Solo AI module to generate groups of similar needs",
    },
    specimenTitle: {
        id: "specimen-student-details-helper-title",
        defaultMessage: "The student record",
    },
    specimenDescription: {
        id: "specimen-student-details-helper-description",
        defaultMessage:
            "The student record presents all the information concerning the work of a particular student: the time spent on the resource/platform, the group to which the student belongs for each module, the details of his/her individual progress as well as his/her successes and difficulties.",
    },
});
